import metadata from './parameterMetadata.json';

export type FeatureType = 'standard' | 'advanced' | 'mixed' | 'experimental';

export interface ParameterValueMetadata {
  feature_type: FeatureType;
  premium: boolean;
  ai_powered: boolean;
  notes?: string;
}

export interface ParameterMetadata {
  feature_type: FeatureType;
  premium: boolean;
  ai_powered: boolean;
  documentation_url: string;
  notes?: string;
  values?: Record<string, ParameterValueMetadata>;
}

/**
 * Get metadata for a specific imgix parameter
 * @param paramName The name of the parameter to get metadata for
 * @returns The parameter metadata or undefined if not found
 */
export function getParameterMetadata(paramName: string): ParameterMetadata | undefined {
  const param = metadata.metadata[paramName as keyof typeof metadata.metadata];
  if (!param) return undefined;
  
  // Ensure feature_type is a valid FeatureType
  if (typeof param.feature_type === 'string' && 
      !['standard', 'advanced', 'mixed', 'experimental'].includes(param.feature_type)) {
    return undefined;
  }
  
  return param as ParameterMetadata;
}

/**
 * Get metadata for a specific imgix parameter value
 * @param paramName The name of the parameter
 * @param value The value to get metadata for
 * @returns The parameter value metadata or undefined if not found
 */
export function getParameterValueMetadata(paramName: string, value: string): ParameterValueMetadata | undefined {
  const param = getParameterMetadata(paramName);
  if (param?.values && param.values[value]) {
    return param.values[value];
  }
  return undefined;
}

/**
 * Check if a parameter is AI-powered based on the parameter itself or specific values
 * @param paramName Parameter name to check
 * @param value Optional parameter value to specifically check
 * @returns True if the parameter or specified value is AI-powered
 */
export function isAiParameter(paramName: string, value?: string): boolean {
  // If value is provided, check if that specific value is AI-powered
  if (value !== undefined) {
    const valueMetadata = getParameterValueMetadata(paramName, value);
    if (valueMetadata) {
      return valueMetadata.ai_powered;
    }
  }
  
  // Check if the parameter itself is AI-powered
  const param = getParameterMetadata(paramName);
  return param?.ai_powered || false;
}

/**
 * Check if a parameter requires premium access based on the parameter itself or specific values
 * @param paramName Parameter name to check
 * @param value Optional parameter value to specifically check
 * @returns True if the parameter or specified value requires premium access
 */
export function isPremiumParameter(paramName: string, value?: string): boolean {
  // If value is provided, check if that specific value requires premium
  if (value !== undefined) {
    const valueMetadata = getParameterValueMetadata(paramName, value);
    if (valueMetadata) {
      return valueMetadata.premium;
    }
  }
  
  // Check if the parameter itself requires premium
  const param = getParameterMetadata(paramName);
  return param?.premium || false;
}

/**
 * Check if a parameter or value is advanced or experimental (not standard)
 * @param paramName Parameter name to check
 * @param value Optional parameter value to specifically check
 * @returns True if the parameter or specified value is advanced/experimental
 */
export function isAdvancedParameter(paramName: string, value?: string): boolean {
  // If value is provided, check that specific value
  if (value !== undefined) {
    const valueMetadata = getParameterValueMetadata(paramName, value);
    if (valueMetadata) {
      return valueMetadata.feature_type === 'advanced' || 
             valueMetadata.feature_type === 'experimental';
    }
  }
  
  // Check the parameter itself
  const param = getParameterMetadata(paramName);
  if (!param) return false;
  
  // Handle mixed type parameters (like fill that has both standard and advanced values)
  if (param.feature_type === 'mixed') {
    return false; // The parameter itself isn't fully advanced
  }
  
  return param.feature_type === 'advanced' || 
         param.feature_type === 'experimental';
}

/**
 * Get the documentation URL for a parameter
 * @param paramName Parameter name
 * @returns The documentation URL or undefined if not found
 */
export function getParameterDocUrl(paramName: string): string | undefined {
  return getParameterMetadata(paramName)?.documentation_url;
}

/**
 * Get all parameters that match specific criteria
 * @param criteria Optional filter criteria
 * @returns Array of parameter names that match the criteria
 */
export function getParametersByType({
  featureType,
  isPremium,
  isAiPowered
}: {
  featureType?: FeatureType;
  isPremium?: boolean;
  isAiPowered?: boolean;
} = {}): string[] {
  return Object.entries(metadata.metadata)
    .filter(([_, data]) => {
      if (featureType !== undefined && data.feature_type !== featureType) return false;
      if (isPremium !== undefined && data.premium !== isPremium) return false;
      if (isAiPowered !== undefined && data.ai_powered !== isAiPowered) return false;
      return true;
    })
    .map(([name]) => name);
}

export default metadata;